
import EoIcon, { EoIconOptions, ICON_SIZES } from '../EoIcon/EoIcon'

export default {
  name: 'EoCheckbox',
  components: { EoIcon },
  inheritAttrs: false, // place additional attrs not on container element, but on <input>
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    /**
     * State of the checkbox
     */
    value: {
      type: [String, Boolean, Array],
      default: null,
    },
    /**
     * Name to attach the value to (when it is not in a checkbox group)
     */
    name: {
      type: String,
      default: null,
    },
    /**
     * Position button inline in text flow
     */
    inline: {
      type: Boolean,
      default: false,
    },
    /**
     * Prevent user interaction with the checkbox
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Icon to show when checked
     */
    iconOn: {
      type: String,
      validator: prop => {
        return EoIconOptions().includes(prop) || prop === undefined || prop === null
      },
      default: null,
    },
    /**
     * Icon to show when not checked
     */
    iconOff: {
      type: String,
      validator: prop => {
        return EoIconOptions().includes(prop) || prop === undefined || prop === null
      },
      default: null,
    },
    /**
     * Size of the icon: (xs) (sm) (md) (lg) (xl)
     */
    size: {
      type: String,
      validator: prop => Object.values(ICON_SIZES).includes(prop),
      default: 'sm',
    },
    /**
     * Style: color of the active checkbox
     */
    color: {
      type: String,
      default: 'primary',
    },
    /**
     * Visually hide the label, but make it visible for screen readers
     */
    labelHidden: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      id: null,
      isFocus: false,
    }
  },
  computed: {
    classes () {
      return {
        'eo-checkbox': true,
        'is-checked': this.isChecked,
        'is-disabled': this.disabled,
        'is-inline': this.inline,
        'is-focus': this.isFocus && !this.disabled,
        ['is-size-' + this.size]: !!this.size,
      }
    },
    groupValue () {
      return (this.isStandalone ? this.value : this.$parent.value) || []
    },
    listeners () {
      return {
        ...this.$listeners,
        input: (e) => {
          /**
           * Emitted when the state has been altered
           * @event input
           */
          // Send event from this checkbox that we have changed
          this.$emit('input', e.target.checked)

          if (!this.isStandalone) {
            let newGroupValue = this.groupValue
            if (!e.target.checked) {
              // Checkbox is deselected. Remove from group value
              newGroupValue = this.groupValue.filter(e => e !== this.value)
            } else if (this.groupValue.indexOf(this.value) === -1) {
              // Checkbox is selected. Add to group value
              newGroupValue = [...this.groupValue, this.value]
            }

            // Send event from parent that it has changed
            this.$parent.$emit('input', newGroupValue)
          }
          this.trackFocus(true)
        },
        focus: (e) => {
          /**
           * Emitted when the user focusses on the field
           * @event focus
           */
          this.$emit('focus', e)
          if (!this.isStandalone) this.$parent.$emit('focus', e)
          this.trackFocus(true)
        },
        blur: (e) => {
          /**
           * Emitted when the user removes focus from the radio button
           * @event blur
           */
          this.$emit('blur', e)
          if (!this.isStandalone) this.$parent.$emit('blur', e)
          this.trackFocus(false)
        },
      }
    },
    isStandalone () {
      // Is this checkbox standalone, or does it reside inside a group?
      return this.$parent.$options.name != 'EoCheckboxGroup'
    },
    computedName () {
      if (this.isStandalone) {
        return this.name || this.id
      } else {
        return this.$parent.computedName || this.name || this.id
      }
    },
    messagesId () {
      if (this.isStandalone) {
        return null
      } else {
        // Refer to messages field in parent component
        return this.$parent.messagesId
      }
    },
    isChecked () {
      return (Array.isArray(this.groupValue) && this.groupValue.includes(this.value)) || this.value === true
    },
  },
  mounted () {
    this.id = `checkbox${this.name ? '-' + this.name : ''}-${this._uid}`
  },
  methods: {
    trackFocus (value) {
      this.isFocus = value
    },
  },
}
